import { Cache } from "@escapio/portal/state/Cache";
import { getRandomInt } from "@escapio/shared/util/random";

/** @template T */
export class RandomSelector {
	#cache;
	#items;
	#storageKey;
	#preselectedId;
	#predicates;

	constructor(storageKey, ttl = 24 * 60 * 60 * 1000) {
		this.#items = [];
		this.#storageKey = storageKey;
		this.#cache = new Cache(this.#storageKey, ttl);
		this.#cache.gc();
		this.#predicates = [];
	}

	/**
	 * @param {T[]} candidates
	 * @returns {RandomSelector}
	 */
	setCandidates(candidates) {
		this.#items = candidates;
		return this;
	}

	/**
	 * @param {int} preselectedId
	 * @returns {RandomSelector}
	 */
	setPreselected(preselectedId) {
		this.#preselectedId = preselectedId;
		return this;
	}

	/**
	 * @param {function(T): Boolean} predicate
	 * @returns {RandomSelector}
	 */
	fulfillsCondition(predicate) {
		this.#predicates.push(predicate);
		return this;
	}

	/** @returns {T|null} */
	select() {
		if (this.#preselectedId) {
			const item = this.#items.find(({ id }) => id === this.#preselectedId);
			if (item) {
				return this.#choose(item);
			}
		}

		const items = this.#items.filter(item =>
			this.#predicates.every(fn => fn(item)),
		);

		if (items.length === 1) {
			return this.#choose(items[0]);
		}

		if (!items.length) {
			return null;
		}

		const lastSeenId = this.#cache.get(this.#storageKey);
		const candidates = items.filter(({ id }) => id !== lastSeenId);
		const selected = getRandomInt(0, candidates.length);
		return this.#choose(candidates[selected]);
	}

	#choose(item) {
		this.#cache.set(this.#storageKey, item?.id);
		return item;
	}
}
